// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessClaimVoucher.jsx
// Название: Business Claim Voucher - DARY
// Версия: 1.0.0
// Дата: 03.12.2025

import React, { useState, useRef } from 'react';
import axios from 'axios';
import './BusinessVoucher.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const BusinessClaimVoucher = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [voucherInfo, setVoucherInfo] = useState(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('authToken');

  // Декодировать токен из картинки
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Выберите изображение PNG или JPEG');
      return;
    }

    setError('');
    setDecoding(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post(API_URL + '/api/vouchers/decode-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success && res.data.data.voucherToken) {
        setCode(res.data.data.voucherToken);
        await checkVoucherByToken(res.data.data.voucherToken);
      } else {
        setError('Не удалось распознать подарок в изображении');
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Ошибка распознавания';
      setError(errMsg === 'No steganographic data found' 
        ? 'Изображение не содержит данных подарка' 
        : errMsg);
    } finally {
      setDecoding(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const checkVoucherByToken = async (voucherToken) => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL + '/api/vouchers/' + voucherToken + '/info', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        setVoucherInfo(res.data.data);
      } else {
        setError(res.data.error || 'Подарок не найден');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Подарок не найден');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVoucher = async () => {
    if (!code.trim()) {
      setError('Введите код подарка');
      return;
    }

    setError('');
    setVoucherInfo(null);
    setLoading(true);

    try {
      const isToken = code.length > 20;
      const endpoint = isToken 
        ? '/api/vouchers/' + code + '/info'
        : '/api/vouchers/code/' + code + '/info';
      
      const res = await axios.get(API_URL + endpoint, {
        headers: { Authorization: 'Bearer ' + token }
      });

      if (res.data.success) {
        setVoucherInfo(res.data.data);
      } else {
        setError(res.data.error || 'Подарок не найден');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Подарок не найден');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    setError('');
    setLoading(true);

    try {
      const isToken = code.length > 20;
      const endpoint = isToken ? '/api/vouchers/claim' : '/api/vouchers/claim-code';
      const body = isToken ? { voucherToken: code } : { fallbackCode: code };

      const res = await axios.post(API_URL + endpoint, body, {
        headers: { Authorization: 'Bearer ' + token }
      });

      if (res.data.success) {
        setSuccess(res.data.data);
        setVoucherInfo(null);
        setCode('');
      } else {
        setError(res.data.error || 'Ошибка активации');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка активации подарка');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCode('');
    setError('');
    setSuccess(null);
    setVoucherInfo(null);
  };

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleString('ru-RU');
  };

  return (
    <div className="biz-claim-container">
      <div className="biz-claim-card">
        <h2>📥 Получить подарок</h2>

        {error && <div className="biz-error-message">{error}</div>}

        {success ? (
          <div className="biz-claim-success">
            <div className="biz-success-icon">✓</div>
            <h3>Подарок получен!</h3>
            <p className="biz-success-amount">+{parseFloat(success.amount).toFixed(2)} DAR</p>
            <p className="biz-success-text">Средства зачислены на ваш баланс</p>
            <button onClick={resetForm} className="biz-btn-new">
              Получить ещё
            </button>
          </div>
        ) : (
          <>
            {/* Загрузка картинки */}
            <div className="biz-upload-section">
              <label className="biz-upload-label">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                  disabled={decoding || loading}
                  style={{ display: 'none' }}
                />
                <div className="biz-upload-btn">
                  {decoding ? '⏳ Распознавание...' : '📷 Загрузить картинку подарка'}
                </div>
              </label>
              <p className="biz-upload-hint">или введите код вручную</p>
            </div>

            {/* Ручной ввод кода */}
            <div className="biz-input-section">
              <div className="biz-form-group">
                <label>Код подарка</label>
                <input
                  type="text"
                  value={code.length > 20 ? code.slice(0, 8) + '...' : code}
                  onChange={(e) => setCode(e.target.value.trim())}
                  placeholder="Введите код (напр. A7F9-K2M3)"
                  disabled={loading || decoding}
                />
              </div>
              <button 
                onClick={handleCheckVoucher} 
                disabled={loading || decoding || !code.trim()}
                className="biz-btn-check"
              >
                {loading ? 'Проверка...' : 'Проверить'}
              </button>
            </div>

            {voucherInfo && (
              <div className="biz-voucher-preview">
                <h3>Информация о подарке</h3>
                <div className="biz-preview-details">
                  <div className="biz-preview-row">
                    <span>Сумма:</span>
                    <strong className="biz-preview-amount">{parseFloat(voucherInfo.amount).toFixed(2)} DAR</strong>
                  </div>
                  <div className="biz-preview-row">
                    <span>Статус:</span>
                    <strong className={'biz-preview-status status-' + voucherInfo.status}>
                      {voucherInfo.status === 'active' ? 'Активен' : voucherInfo.status}
                    </strong>
                  </div>
                  {voucherInfo.ttl && (
                    <div className="biz-preview-row">
                      <span>Действителен до:</span>
                      <strong>{formatDate(voucherInfo.ttl)}</strong>
                    </div>
                  )}
                </div>

                {voucherInfo.status === 'active' ? (
                  <button 
                    onClick={handleClaim} 
                    disabled={loading}
                    className="biz-btn-claim"
                  >
                    {loading ? 'Активация...' : '✓ Получить средства'}
                  </button>
                ) : (
                  <div className="biz-voucher-unavailable">
                    Этот подарок недоступен для активации
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BusinessClaimVoucher;
